import { forwardRef, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { IconButton } from './IconButton.jsx';
import { mergeRefs, useFocusTrap } from './useFocusTrap.js';

/**
 * A modal side/bottom sheet (spec §7.2, §12) — same focus-trap, Escape and
 * scrim contract as Dialog, laid out along an edge instead of centred.
 * `data-side="bottom"` is the mobile action-sheet form and shows the grab
 * handle from overlays.css.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} [props.onClose]
 * @param {import('react').ReactNode} [props.title]
 * @param {import('react').ReactNode} props.children
 * @param {'start'|'end'|'bottom'} [props.side='end']
 * @param {boolean} [props.dismissible=true]
 * @param {string} [props.closeLabel='Close'] - accessible name for the
 *   built-in close button. ALWAYS pass a translated string.
 * @param {import('react').RefObject<HTMLElement>} [props.initialFocusRef]
 * @param {string} [props.className]
 */
export const Drawer = forwardRef(function Drawer(
  {
    open,
    onClose,
    title,
    children,
    side = 'end',
    dismissible = true,
    closeLabel = 'Close',
    initialFocusRef,
    className = '',
    ...rest
  },
  ref
) {
  const drawerRef = useRef(null);
  const titleId = useId();

  useFocusTrap({ active: open, containerRef: drawerRef, onClose, initialFocusRef });

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="etch-scrim"
        onClick={() => {
          if (dismissible !== false) onClose?.();
        }}
      />
      <div
        ref={mergeRefs(drawerRef, ref)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        data-side={side}
        className={`etch-drawer ${className}`.trim()}
        {...rest}
      >
        {side === 'bottom' && <div className="etch-drawer__handle" aria-hidden="true" />}
        {(title || dismissible !== false) && (
          <div className="etch-drawer__header">
            {title && (
              <h2 id={titleId} className="etch-dialog__title">
                {title}
              </h2>
            )}
            {dismissible !== false && (
              <IconButton label={closeLabel} onClick={() => onClose?.()}>
                <X size={18} />
              </IconButton>
            )}
          </div>
        )}
        <div className="etch-drawer__body">{children}</div>
      </div>
    </>,
    document.body
  );
});

export default Drawer;
